<script setup lang="ts">
import { ref } from 'vue';
import { useQuasar } from 'quasar';

import type { ChoiceDto, Locales } from '@/services/types/api.types';
import { useFormsStore } from '@/stores';
import { useI18n } from 'vue-i18n';

import ReorderableTable from './ReorderableTable.vue';
import ChoiceEditDialog from './dialogs/ChoiceEditDialog.vue';
import { getTranslation } from '@/composables/data-display-utils';

const $q = useQuasar();
const { t } = useI18n();

const formsStore = useFormsStore();

const props = defineProps<{
  rows: ChoiceDto[];
  formId: number;
  stepId: number;
  canChoicesBeDeleted?: boolean;
  languageEnabled: Locales[];
}>();

const columns = ref([
  {
    name: 'text',
    label: 'Texte',
    align: 'left',
    field: (row: ChoiceDto) => getTranslation(row, 'text', props.languageEnabled),
  },
  { name: 'image', label: 'Image', align: 'center', field: (row: ChoiceDto) => row.image?.url },
]);

const handleRowClick = (row: ChoiceDto) => {
  $q.dialog({
    component: ChoiceEditDialog,
    componentProps: {
      mode: 'edit',
      choice: row,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleAddRow = () => {
  const choice = formsStore.createChoice();
  $q.dialog({
    component: ChoiceEditDialog,
    componentProps: {
      mode: 'add',
      choice,
      formId: props.formId,
      stepId: props.stepId,
      languageEnabled: props.languageEnabled,
    },
  });
};

const handleDeleteRow = (row: ChoiceDto) => {
  $q.dialog({
    title: t('form.dialogs.delete-choice-title'),
    message: t('form.dialogs.delete-choice-message'),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => {
    formsStore.deleteChoice(row, props.formId, props.stepId);
  });
};

const handleReorder = async (source: ChoiceDto, target: ChoiceDto) => {
  await formsStore.reorderChoice(source, props.formId, props.stepId);
  await formsStore.reorderChoice(target, props.formId, props.stepId);
};
</script>

<template>
  <div>
    <reorderable-table
      :rows="props.rows"
      :columns="columns"
      :title="t('global.label.answers')"
      itemKey="id"
      :disable-delete-button="true"
      :action-menu-items="[
        {
          action: 'delete-row',
          label: t('global.label.delete'),
          disabled: !props.canChoicesBeDeleted,
          tooltip: !props.canChoicesBeDeleted ? t('global.notifications.choice-prohibit-delete') : undefined,
        },
      ]"
      @rowClick="handleRowClick"
      @reorder="handleReorder"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
    >
      <template v-slot:body-cell-image="props">
        <q-td :props="props">
          <img :src="props.row.image?.url" style="max-height: 48px" />
        </q-td>
      </template>
    </reorderable-table>
  </div>
</template>
